import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { CreditCard, Lock, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { saasApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';

export default function Checkout() {
  const { planId } = useParams();
  const { user, firm, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvv: '' });

  useEffect(() => { saasApi.plans().then(setPlans).catch(() => {}); }, []);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#52676e]">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!firm) return <div className="min-h-[60vh] flex items-center justify-center text-[#52676e]">Nenhum escritório vinculado à sua conta.</div>;

  const plan = plans.find(p => p.id === planId);
  if (plans.length > 0 && !plan) return <Navigate to="/planos" replace />;

  const confirm = async () => {
    setProcessing(true);
    try {
      await saasApi.checkout(planId);
      await refresh();
      toast({ title: 'Assinatura ativada!', description: `Plano ${plan.name} ativo (simulação — nada foi cobrado).` });
      navigate('/painel?tab=assinatura');
    } catch (err) {
      toast({ title: 'Erro', description: err.response?.data?.detail || 'Falha no checkout.', variant: 'destructive' });
    } finally { setProcessing(false); }
  };

  return (
    <div className="aj-wrap max-w-[760px] py-10">
      <SEO title="Checkout de assinatura" description="Finalize sua assinatura." noIndex />
      <Link to="/planos" className="inline-flex items-center gap-1 text-sm text-[#425a62] hover:text-[#153e44] mb-4"><ArrowLeft className="w-4 h-4" /> Voltar aos planos</Link>
      <h1 className="text-2xl md:text-3xl font-semibold text-[#153e44]">Finalizar assinatura</h1>

      <div className="mt-3 bg-amber-50 border border-amber-200 rounded-[7px] p-4 text-sm text-amber-800" data-testid="checkout-mock-banner">
        <strong>Modo demonstração:</strong> este checkout é simulado. Nenhum valor será cobrado e nenhum dado de cartão é validado ou armazenado.
      </div>

      {plan && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_280px] gap-6">
          <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-6">
            <h3 className="font-semibold text-[#153e44] mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#075a6d]" /> Dados do cartão (simulados)</h3>
            <div className="space-y-3">
              <Input data-testid="checkout-card-number" placeholder="Número do cartão (ex: 4242 4242 4242 4242)" value={card.number} onChange={e => setCard({ ...card, number: e.target.value })} />
              <Input data-testid="checkout-card-name" placeholder="Nome impresso no cartão" value={card.name} onChange={e => setCard({ ...card, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="MM/AA" value={card.expiry} onChange={e => setCard({ ...card, expiry: e.target.value })} />
                <Input placeholder="CVV" value={card.cvv} onChange={e => setCard({ ...card, cvv: e.target.value })} />
              </div>
            </div>
            <Button onClick={confirm} disabled={processing} className="w-full mt-5 h-11 bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold" data-testid="checkout-confirm-btn">
              {processing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ativando...</>) : (<><Lock className="w-4 h-4 mr-2" /> Confirmar assinatura (simulação)</>)}
            </Button>
            <p className="text-xs text-[#52676e] mt-3 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Cancelamento self-service a qualquer momento no painel.</p>
          </div>
          <div className="bg-white border border-[#cbd8dc] text-[#243b42] rounded-[10px] p-6 h-fit">
            <div className="text-xs font-semibold text-[#075a6d] uppercase tracking-widest">{plan.tagline}</div>
            <h3 className="text-xl font-semibold mt-1">Plano {plan.name}</h3>
            <div className="mt-3 text-3xl font-semibold">R${plan.price.toLocaleString('pt-BR')}<span className="text-sm font-normal text-[#425a62]">/mês</span></div>
            <ul className="mt-4 space-y-1.5 text-sm text-[#354c54]">
              {plan.features.map((f, i) => <li key={i}>• {f}</li>)}
            </ul>
            <div className="mt-4 pt-4 border-t border-[#cbd8dc] text-xs text-[#425a62]">Escritório: {firm.name}</div>
          </div>
        </div>
      )}
    </div>
  );
}
